import { DOCUMENT, isPlatformServer } from '@angular/common';
import { CommonModule } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { Component, ElementRef, Inject, Input, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ImagePreloadDirective } from '@core-mkt/services/image-preload/image-preload.directive';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { TrustpilotService } from '@core-mkt/services/trustpilot/trustpilot.service';
import { isValidWistiaId } from '@core-mkt/services/wistia/server-wistia.service';
import { WistiaService } from '@core-mkt/services/wistia/wistia.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { ProductPickerComponent } from '@core-mkt/shared/components/product-picker/product-picker/product-picker.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TrustpilotInfoText } from '../../top-section/top-section/trustpilot/trustpilot-data';
import type { ButtonHomePage, HeroVideoButton, ProductPicker, StyleObj } from './hero-homepage';
import { HomepageHero } from './hero-homepage';

@Component({
  selector: 'hero-homepage',
  templateUrl: './hero-homepage.component.html',
  styleUrls: ['./hero-homepage.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    ProductPickerComponent,
    ImagePreloadDirective,
    LazyImgDirective,
    QueryParamDirective,
  ],
})
export class HeroHomepageComponent implements OnInit, AfterViewInit {
  @Input() data: HomepageHero;
  @ViewChild('heroImg') heroImg: ElementRef;
  theme: ThemeInterface;
  faTimes = faTimes;
  imgHeight = 0;
  hasContent = true;
  heroStyle: StyleObj;
  faChevronRight = faChevronRight;
  trustpilotTrustScore: number;
  trustpilotNumberOfReviews: number;
  trustpilotInfoText = TrustpilotInfoText;
  trustpilotVerified = false;
  /** First Craft video button with a Wistia ID; drives the video CTA and its background treatment. */
  videoCta: HeroVideoButton | null = null;

  constructor(
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private tps: TrustpilotService,
    private env: EnvService,
    private accessibilityService: AccessibilityService,
    private renderer: Renderer2,
    // Resolves to the inert ServerWistiaService during SSR (see app.server.module.ts).
    private wistia: WistiaService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  private injectPreloadLink(src: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', src);
    this.renderer.appendChild(this.document.head, link);
  }

  get contentWidth(): string {
    if (this.data.heroImage.length > 0) {
      return 'w-full md:w-1/2';
    }
    return 'w-full';
  }

  get imgWidth(): string {
    if (this.data.heroImage.length > 0) {
      return 'w-full md:w-1/2';
    }
    return 'w-full';
  }

  get buttons(): ButtonHomePage[] {
    return this.data.buttonList ? this.data.buttonList : this.data.aarpButtonList;
  }

  get isImageAlignmentToLeft(): boolean {
    return this.data.imageAlignment === 'flex-row-reverse';
  }

  get showImageOnMobile(): boolean {
    return this.data.showImageOnMobile;
  }

  get imageBleedClass(): string {
    if (!this.isImageAlignmentToLeft) {
      return 'left-img-bleed';
    }
    return '';
  }

  /**
   * Gets the content of the product picker.
   * @returns The first element of the productPicker array from Craft
   */
  get productPickerContent(): ProductPicker[] {
    if (
      this.data.productPickerTable &&
      this.data.productPickerTable.length > 0 &&
      this.data.productPickerTable[0].productPickerTable
    ) {
      return this.data.productPickerTable;
    }
  }

  get heroImageUrl(): string {
    if (this.data.heroImage[0].url.includes('auto=compress,format')) {
      return this.data.heroImage[0].url;
    }
    return `${this.data.heroImage[0].url}&auto=compress,format`;
  }

  get TrustScoreImageUrl(): string {
    if (this.data.TrustScoreImage && this.data.TrustScoreImage[0].url) {
      if (this.data.TrustScoreImage[0].url.includes('auto=compress,format')) {
        return this.data.TrustScoreImage[0].url;
      }
      return `${this.data.TrustScoreImage[0].url}&auto=compress,format`;
    }
    return 'https://xgrit-ecom.imgix.net/ace/trustpilot-star-rating.svg?auto=compress,format&v=1706889351';
  }

  get TrustScoreImageAltText(): string {
    if (this.data.TrustScoreImage && this.data.TrustScoreImage[0].altText) {
      return this.data.TrustScoreImage[0].altText;
    }
    return 'TrustScore 5 out of 5 Stars';
  }

  get styleButtonsList(): string {
    let styleClass: string;
    switch (this.data.buttonListStyle) {
      case 'primaryButtonsWithArrow':
        styleClass = 'primary-button';
        break;
      case 'secondaryButtonsWithArrow':
        styleClass = 'secondary-button';
        break;
      case 'tertiaryButtonsWithArrow':
        styleClass = 'tertiary-button';
        break;
      default:
        styleClass = 'primary-button';
        break;
    }
    return styleClass;
  }

  ngOnInit(): void {
    if (!this.data) {
      this.hasContent = false;
      return;
    }
    if (this.data.heroContent === null) {
      this.data.heroContent = '';
    } else {
      const src = isPlatformServer(this.platformId) ? this.redactor.extractFirstImageSrc(this.data.heroContent) : null;
      this.data.heroContent = this.redactor.bypassSanitizer(this.data.heroContent, 'lcp');
      if (src) this.injectPreloadLink(src);
    }

    if (this.data.heroTitle === null) {
      this.data.heroTitle = '';
    } else {
      const src = isPlatformServer(this.platformId) ? this.redactor.extractFirstImageSrc(this.data.heroTitle) : null;
      this.data.heroTitle = this.redactor.bypassSanitizer(this.data.heroTitle, 'lcp');
      if (src) this.injectPreloadLink(src);
    }
    this.hasContent = !(this.data.heroContent.length === 0 && this.data.heroImage.length === 0);
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage[0],
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: this.data.imageAlignment,
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);

    const videoButton = this.data.videoButton?.[0];
    // A pasted URL or an ID with `-` would pass a truthy check, but WistiaService refuses it,
    // leaving a CTA that opens nothing. Keep the normal hero unless the ID is one it will play.
    if (videoButton?.wistiaId && isValidWistiaId(videoButton.wistiaId)) {
      this.videoCta = { ...videoButton, ctaLabel: videoButton.ctaLabel || 'Watch Overview' };
      this.wistia.insertWistiaCtaData(videoButton.wistiaId);
    }

    if (this.data.showTrustpilot && this.env.get.brandConfig.trustpilotId.length > 0) {
      void this.tps.getBrandStarsRating().then((trustPilotRating) => {
        if (!trustPilotRating) return;
        this.trustpilotTrustScore = trustPilotRating.score.trustScore;
        this.trustpilotNumberOfReviews = trustPilotRating.numberOfReviews.total;
        this.trustpilotVerified = this.trustpilotNumberOfReviews > 0;
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.heroImg) {
      //a minute delay must be granted to allow image to render
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.imgHeight = this.showImageOnMobile ? this.heroImg.nativeElement.offsetHeight : 0;

        const styleObj: StyleObj = {};
        if (this.imgHeight !== 0) {
          styleObj['min-height.px'] = this.imgHeight;
        }
        // The video hero's background is its own design treatment (component SCSS); an inline
        // CMS background here would outrank it.
        if (!this.videoCta && this.data.backgroundImage.length > 0 && this.data.backgroundImage[0].url !== null) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const bgUrl: string = this.data.backgroundImage[0].url;
          styleObj['background-image'] = `url(${bgUrl})`;
        }
        this.heroStyle = styleObj;
      }, 25);
    }
  }

  playVideo(event: Event): void {
    if (!this.videoCta) return;
    this.wistia.openCtaPopover(event.currentTarget as Element, this.videoCta.wistiaId);
  }

  getIdToAriaLabelledbyContext(label: string): string {
    const cleanText = this.accessibilityService.getAriaLabelledby(label);
    return cleanText + '-context';
  }
}
